import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function EnrollmentAdminPage() {
  const { t, lang } = useI18n();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");

  const load = () => Promise.all([
    api.get("/classes").then((r) => setClasses(r.data.classes)),
    api.get("/students").then((r) => setStudents(r.data.students)),
    api.get("/enrollments").then((r) => setEnrollments(r.data.enrollments)),
  ]);

  useEffect(() => { load(); }, []);

  const enroll = async () => {
    if (!selectedClass || !selectedStudent) return toast.error("Pick a class and a student.");
    try {
      await api.post("/enrollments", { class_id: selectedClass, student_id: selectedStudent });
      toast.success(lang === "ms" ? "Berjaya didaftarkan" : "Enrolled");
      setSelectedStudent("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unenroll = async (id) => {
    if (!window.confirm(lang === "ms" ? "Nyahdaftar?" : "Remove enrollment?")) return;
    try { await api.delete(`/enrollments/${id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const byClass = useMemo(() => {
    const m = {};
    enrollments.forEach((e) => { (m[e.class_id] = m[e.class_id] || []).push(e); });
    return m;
  }, [enrollments]);

  return (
    <div className="space-y-6" data-testid="admin-enrollment">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("enrollment")}</h1>
        <p className="text-sm text-muted-foreground">
          {lang === "ms"
            ? "Daftarkan setiap murid ke kelas tertentu supaya ibu bapa lihat jadual yang berkaitan sahaja."
            : "Enroll each student into specific classes so parents only see the schedule relevant to their child."}
        </p>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-6">
          <div className="grid gap-3 md:grid-cols-[2fr_2fr_auto]">
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t("chooseClass")}</label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger data-testid="enr-class" className="h-11 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {DAYS[c.day_of_week]} {c.start_time} · {c.level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t("chooseStudent")}</label>
              <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                <SelectTrigger data-testid="enr-student" className="h-11 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={enroll} className="rounded-full h-11 gap-2 md:mt-6" data-testid="enroll-btn">
              <UserPlus className="h-4 w-4" /> {t("enroll")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {classes.map((c) => (
          <Card key={c.id} className="rounded-2xl" data-testid={`enr-class-card-${c.id}`}>
            <CardContent className="p-6">
              <div className="mb-2">
                <div className="font-display text-lg font-semibold">{c.name}</div>
                <div className="num text-xs text-muted-foreground">
                  {DAYS[c.day_of_week]} · {c.start_time}–{c.end_time} · {c.level}
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {(byClass[c.id] || []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-1.5 text-sm">
                    <span>{e.student_name}</span>
                    <Button size="sm" variant="ghost" onClick={() => unenroll(e.id)} data-testid={`unenroll-${e.id}`} className="h-7 w-7 p-0">×</Button>
                  </div>
                ))}
                {(byClass[c.id] || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">{lang === "ms" ? "Tiada murid" : "No students"}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
